import { cn } from '@/lib/utils';
import './setup.css';

const SIZES = { xs: 16, sm: 22, md: 28, lg: 36, xl: 56, hero: 110 } as const;

export interface OrbProps {
  /** Pixels, or a preset: xs 16, sm 22, md 28 (avatar), lg 36 (headers), xl 56, hero 110 (Go live). */
  size?: number | keyof typeof SIZES;
  /** Slow scale in and out, for idle hero states. */
  breathing?: boolean;
  /** Faster shimmer and a pulse, e.g. while the AI is writing. */
  active?: boolean;
  /** Soft halo and depth, for the large hero orb. */
  glow?: boolean;
  className?: string;
  /** Accessible name; decorative (hidden) when omitted. */
  label?: string;
}

/**
 * The AI avatar: a lit sphere with a slow conic shimmer. Pure CSS, no image. The outer box takes
 * the size, `className` and the breathing or pulse; the sphere inside spins its shimmer.
 */
export function Orb({ size = 'md', breathing, active, glow, className, label }: OrbProps) {
  const px = typeof size === 'number' ? size : SIZES[size];
  const props = {
    className: cn('sw-orb-box', className),
    style: { width: px, height: px },
    'data-breathing': breathing ? 'true' : undefined,
    'data-active': active ? 'true' : undefined,
  };
  const sphere = (
    <span
      className="sw-orb"
      data-active={active ? 'true' : undefined}
      data-glow={glow ? 'true' : undefined}
    />
  );
  return label ? (
    <span role="img" aria-label={label} {...props}>
      {sphere}
    </span>
  ) : (
    <span aria-hidden="true" {...props}>
      {sphere}
    </span>
  );
}
